/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    string, bool, func, node, object,
} from 'prop-types';
import qs from 'qs';
import mbpLogger from 'mbp-logger';

import withStyles from '@material-ui/core/styles/withStyles';
import {
    ClickAwayListener, MenuItem, MenuList, Paper,
} from '@material-ui/core';

// TODO migrate QAS failed
import { flagOnQasFailed } from '../../../../../../../state/ducks/Checkout/ducks/Common/ducks/AddressSearchQAS/AddressSearchQAS-Actions';
import { trackEvent } from '../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { getFeatureFlags } from '../../../../../../../state/ducks/MfeCheckout/ducks/App/App-Selectors';
import { getOrderId } from '../../../../../../../state/ducks/Common/Common-Selectors';

import { usePrevious } from '../Helpers/pageHelpers';
import getSearchQAS from '../../../../../../../apis/qas-address-search-apis/getSearchQAS';
import getFormatQAS from '../../../../../../../apis/qas-address-search-apis/getFormatQAS';
import { emitClickstreamCheckoutEvent } from '../../../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';

const styles = (theme) => ({
    root: {
        [theme.breakpoints.down('sm')]: {
            minHeight: '2.2em',
        },
    },
    wrapSuggestion: {
        position: 'relative',
    },
    autoSuggestion: {
        position: 'absolute',
        backgroundColor: '#FFF',
        zIndex: 3000,
        width: '100%',
        padding: '0px 3px',
        maxHeight: '14rem',
        overflow: 'auto',
        [theme.breakpoints.down('sm')]: {
            '&::-webkit-scrollbar': {
                '-moz-appearance': 'none',
                '-webkit-appearance': 'none',
                background: '#C4C4C4',
                width: '10px',
            },
            '&::-webkit-scrollbar-thumb': {
                borderRadius: '4px',
                backgroundColor: '#7B7B7B',
                boxShadow: '0 0 1px #7B7B7B',
            },
        },

    },
    addressList: {
        padding: '4px',
        fontWeight: '600',
        borderBottom: 'solid 1px #ecdede',
        fontSize: '0.9em',
    },
    menuListWrapper: {
        padding: '0px',
    },
});

const countryISOAlfa3Code = {
    US: 'USA',
    CA: 'CAN',
};

const AddressVerificationSearchV2 = (props) => {
    const {
        children,
        classes,
        countryCode,
        getPreFillAddressDetails,
        isFocus,
        onBlurHandler,
        pageType,
        searchId,
        searchText,
        searchTextFocus,
        state,
        useGeoLocation,
        zipCode,
    } = props;
    const dispatchAction = useDispatch();

    const featureFlags = useSelector(getFeatureFlags);
    const cartId = useSelector(getOrderId);

    const [autoCompleteResult, setAutoCompleteResult] = useState({});

    const [initGeoLocation, setInitGeoLocation] = useState(false);

    const prevSearchText = usePrevious(searchText);
    const prevCountryCode = usePrevious(countryCode);

    const handleNoMatch = () => {
        setAutoCompleteResult({});
        onBlurHandler();
    };

    const handleQASAddressFailed = () => {
        dispatchAction(flagOnQasFailed());

        dispatchAction(trackEvent({
            eventCategory: 'Checkout',
            eventAction: 'Address Verification Search',
            eventLabel: `QAS Failed - V2 - ${searchId}`,
        }));

        setTimeout(() => {
            if (typeof document !== 'undefined' && document.getElementById(searchId)) {
                document.getElementById(searchId).focus();
            }
        }, 200);
    };

    const searchAddress = () => {
        const searchTimeout = featureFlags['qas-address-search-timeout-value'] || 5;
        const whichQASContext = featureFlags['which-qas-context']; // state, state and zipcode or zipcode only
        const timeoutInSecs = searchTimeout * 1000;
        let addressSearchText = searchText || '';
        if (searchText.length >= 3) {
            let isoCountryCode = countryCode;
            if (countryCode && countryCode.length !== 3) {
                isoCountryCode = countryISOAlfa3Code[countryCode];
            }

            if (isoCountryCode) {
                const searchZipCode = zipCode ? zipCode.split('-')[0] : '';
                addressSearchText = addressSearchText.trim();

                if (whichQASContext === 'state' && state) {
                    addressSearchText += `, ${state}`;
                } else if (whichQASContext === 'state-and-zipcode' && state && searchZipCode) {
                    // This has been added because business want see
                    // relevant addresses, if the zipcode is already
                    // populated on the form
                    addressSearchText += `, ${state} ${searchZipCode}`;
                } else if (whichQASContext === 'zipcode' && searchZipCode) {
                    addressSearchText += `, ${searchZipCode}`;
                }

                const payload = {
                    country_iso: isoCountryCode,
                    max_suggestions: 100,
                    components: {
                        unspecified: [addressSearchText],
                    },
                    datasets: [`${isoCountryCode.toLowerCase().substring(0, 2)}-address`],
                };

                getSearchQAS(payload).then((res) => {
                    setAutoCompleteResult(res.data.result);
                }).catch((ex) => {
                    setAutoCompleteResult({});
                    handleQASAddressFailed();
                    mbpLogger.logError({
                        function: 'getSearchQAS',
                        appName: process.env.npm_package_name,
                        module: 'mbp-checkout',
                        message: `Get QAS Address V2 failed ${cartId}`,
                        jsError: ex,
                        orderId: cartId,
                    });
                    console.log('QAS search v2 error:', ex);
                    console.log('QAS search v2 error object:', JSON.stringify(ex));
                    console.log('QAS search v2 error - response:', ex?.response);
                    console.log('QAS search v2 params:', payload);
                });
            }
        } else {
            setAutoCompleteResult({});
        }
    };

    useEffect(() => {
        if (prevSearchText !== searchText && !isFocus) {
            searchAddress();
        }
    }, [searchText, isFocus]);

    useEffect(() => {
        if (useGeoLocation && (prevCountryCode !== countryCode || !initGeoLocation)) {
            setInitGeoLocation(true);
        }
    }, [useGeoLocation, countryCode]);

    const handleKeyDown = (e, index) => {
        if (e.key === 'Tab' && (index + 1) === autoCompleteResult.suggestions?.length) {
            onBlurHandler();
        }
    };

    const returnSelectedAddress = (formattedAddress) => {
        // This function is essential for populating address with QAS data
        const { components, address } = formattedAddress;

        let address1 = address.address_line_1;
        let address2 = `${address.address_line_2} ${address.address_line_3}`.trim();

        // Validating if there is address line 2 suggested for the address with suite, apt, etc
        if (!address2) {
            const addressWithAddressLine2 = autoCompleteResult.suggestions.filter((result) => {
                const addressList = result.text.split(',')?.[0];
                return addressList.indexOf(address1) >= 0 && addressList.length > address1.length;
            });
            if (addressWithAddressLine2.length > 0) {
                address2 = ' ';
            }
        }

        if (['APO', 'FPO'].includes(components.locality?.town.name)) {
            address1 = components.street.full_name;
            address2 = components.secondary_delivery_service.full_name;
        }

        const selectedAddress = {
            addressLine1: address1,
            addressLine2: address2,
            city: address.locality,
            state: address.region,
            country: components.country_iso_3,
            zipcode: components.postal_code.primary,
        };

        dispatchAction(emitClickstreamCheckoutEvent({
            action: 'Click',
            label: 'Suggestion drop down',
            eventType: 'clickstream.checkout-address',
            eventSubType: 'qas-dropdown-selection',
            page: { type: pageType },
            order: { cartId },
            recipients: [
                {
                    userAddress: {
                        address1: searchText,
                        address2: '',
                        city: address.locality,
                        state: address.region,
                        country: components.country_iso_3,
                        zipcode: components.postal_code.primary,
                    },
                    qasAddress: {
                        address1: selectedAddress.addressLine1,
                        address2: selectedAddress.addressLine2,
                        city: selectedAddress.city,
                        state: selectedAddress.state,
                        country: selectedAddress.country,
                        zipcode: selectedAddress.zipCode,
                    },
                    addressSource: searchId,
                    qasSuggestionsCount: autoCompleteResult.count || 0,
                },
            ],
        }));

        getPreFillAddressDetails(selectedAddress);
    };

    const handlePickAnAddress = (address) => {
        if (address && address.format) {
            const payload = {
                global_address_key: address.global_address_key,
            };

            getFormatQAS(payload).then((res) => {
                returnSelectedAddress(res.data.result);
                setAutoCompleteResult({});
                onBlurHandler();
                dispatchAction(trackEvent({
                    eventCategory: 'Checkout',
                    eventAction: 'Selected QAS address list suggestion',
                    eventLabel: 'QAS version 2 - search',
                }));
            }).catch((ex) => {
                setAutoCompleteResult({});
                onBlurHandler();

                mbpLogger.logError({
                    function: 'getFormatQAS',
                    appName: process.env.npm_package_name,
                    module: 'mbp-checkout',
                    message: `Get QAS Address V2 failed ${cartId}`,
                    jsError: ex,
                    orderId: cartId,
                });

                console.log('QAS format v2 error:', ex);
                console.log('QAS format v2 error object:', JSON.stringify(ex));
                console.log('QAS format v2 error - response:', ex?.response);
                console.log('QAS format v2 params:', payload);
            });
        }
    };

    const getSuggestions = () => {
        if (autoCompleteResult.suggestions?.length > 0) {
            return autoCompleteResult.suggestions.map((address, index) => (
                <MenuItem
                    classes={{ root: classes.root }}
                    className={classes.addressList}
                    key={`suggestionAVS${address.text.replace(/\s/g, '')}`}
                    onClick={() => { handlePickAnAddress(address); }}
                    onKeyDown={(e) => { handleKeyDown(e, index); }}
                    tabIndex={0}
                >
                    {address.text}
                </MenuItem>
            ));
        }

        if (autoCompleteResult.suggestions?.length === 0) {
            return (
                <MenuItem
                    className={classes.addressList}
                    key="No_Match_Found"
                    onClick={() => { handleNoMatch(); }}
                >
                    No address match found
                </MenuItem>
            );
        }
        return null;
    };

    return (
        <div id={`auto_${searchId}`}>
            <div>
                {children}
                {
                    (searchTextFocus || isFocus)
                    && (
                        <ClickAwayListener onClickAway={onBlurHandler}>
                            <div className={classes.wrapSuggestion}>
                                <Paper className={classes.autoSuggestion}>
                                    <MenuList className={classes.menuListWrapper} autoFocusItem={isFocus}>
                                        {getSuggestions()}
                                    </MenuList>
                                </Paper>
                            </div>
                        </ClickAwayListener>
                    )
                }
            </div>
        </div>
    );
};

AddressVerificationSearchV2.defaultProps = {
    countryCode: 'USA',
    isFocus: false,
    onBlurHandler: () => { },
    searchTextFocus: false,
    useGeoLocation: false,
    pageType: null,
};

AddressVerificationSearchV2.propTypes = {
    children: node.isRequired,
    classes: object.isRequired,
    countryCode: string,
    getPreFillAddressDetails: func.isRequired,
    isFocus: bool,
    onBlurHandler: func,
    pageType: string,
    searchText: string.isRequired,
    searchTextFocus: bool,
    searchId: string.isRequired,
    useGeoLocation: bool,
    state: string.isRequired,
    zipCode: string.isRequired,
};

export default (withStyles(styles)(AddressVerificationSearchV2));
